/**
 * src/components/Financial/CelestialEventsSection.jsx
 * 【完全版：ダークモード対応・スタイリッシュUI・セクション独立化】
 */

import React, { useMemo, useState, useEffect } from "react";
import * as Astro from "../../astronomy.js";
import {
  PLANET_SYMBOLS,
  DEFAULT_PLANET_COLORS,
} from "../../utils/constants.js";
import WeeklyCalendarSection from "./WeeklyCalendarSection.jsx";
import { styles as commonStyles } from "../../utils/styles.js";
import {
  Sun,
  RefreshCw,
  Activity,
  MoveRight,
  Moon,
  Globe,
  Info,
  Clock,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Zap,
} from "lucide-react";

const QUALITY_STYLE = {
  Cardinal: { bg: "#8b5cf6", label: "CARDINAL(活動宮)" },
  Fixed: { bg: "#c77a0a", label: "FIXED(固定宮)" },
  Mutable: { bg: "#27ae60", label: "MUTABLE(柔軟宮)" },
};

const INGRESS_MARKET_THEMES = {
  Sun: "市場の主要なトレンドやセンチメントの方向性を決定します。",
  Moon: "短期的なボラティリティと個人投資家の心理を反映します。",
  Mercury: "情報の流通速度、通信、短期トレード、アルゴリズムに影響します。",
  Venus: "通貨価値、ラグジュアリー消費、市場の楽観主義と関連します。",
  Mars: "投機的なエネルギー、攻撃的な売買、エネルギーセクターの動向。",
  Jupiter: "強気相場、拡大、インフレ傾向、大規模な資金流入。",
  Saturn: "規制の強化、弱気相場、構造的な調整、ディフェンシブな動き。",
  Uranus: "テクノロジーセクターの急変、突発的ショック、暗号資産の変動。",
  Neptune: "不透明な見通し、バイオ・医薬セクター、過度な幻想。",
  Pluto: "金融システムの根本的変容、巨大資本の移動、債務問題。",
};

export default function CelestialEventsSection(props) {
  const date = props.baseDate;
  const isHeliocentric = props.isHeliocentric;
  const isSidereal = props.isSidereal;
  const showExtendedPoints = props.showExtendedPoints;
  const planetColors = props.planetColors || DEFAULT_PLANET_COLORS;
  const isDarkMode = props.isDarkMode;

  // --- 追加：レスポンシブ判定 (isMobile) の定義 ---
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200,
  );
  useEffect(function () {
    const handleResize = function () {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener("resize", handleResize);
    return function () {
      window.removeEventListener("resize", handleResize);
    };
  }, []);
  const isMobile = windowWidth <= 768;
  // ----------------------------------------------

  // ダークモード判定
  const isDark = (function () {
    const p = props.isDarkMode;
    if (p === true || p === "true" || p === "dark") return true;
    if (typeof document !== "undefined") {
      var el = document.documentElement;
      var bd = document.body;
      if (
        el.classList.contains("dark") ||
        bd.classList.contains("dark") ||
        el.getAttribute("data-theme") === "dark"
      )
        return true;
    }
    return false;
  })();

  const theme = {
    cardBg: isDark ? "#1c1f26" : "#ffffff",
    subBg: isDark ? "rgba(255, 255, 255, 0.03)" : "#f8fafc",
    border: isDark ? "#2d313d" : "#e2e8f0",
    text: isDark ? "#ffffff" : "#1e293b",
    subText: isDark ? "#9ca3af" : "#64748b",
  };

  const [openIngressIndex, setOpenIngressIndex] = useState(null);

  const events = useMemo(
    function () {
      if (!date) return null;
      return Astro.getCelestialEvents(
        date,
        isHeliocentric === true,
        isSidereal === true,
        showExtendedPoints === true,
      );
    },
    [date, isHeliocentric, isSidereal, showExtendedPoints],
  );

  if (events === null)
    return (
      <div
        style={{ padding: "40px", textAlign: "center", color: theme.subText }}
      >
        天体イベントを解析中...
      </div>
    );

  const renderIngressCard = function (ing, index) {
    const isOpen = openIngressIndex === index;
    const signData = ing.toSign;
    const modality =
      signData !== null && signData !== undefined ? signData.modality : "不明";
    const dignity = ing.dignity;
    const borderColor = isHeliocentric === true ? "#e67e22" : "#3498db";

    const formatDateTime = function (d) {
      if (!d) return "";
      var year = d.getFullYear();
      var month = (d.getMonth() + 1).toString().padStart(2, "0");
      var dateVal = d.getDate().toString().padStart(2, "0");
      var hours = d.getHours().toString().padStart(2, "0");
      var minutes = d.getMinutes().toString().padStart(2, "0");
      return year + "/" + month + "/" + dateVal + " " + hours + ":" + minutes;
    };

    return (
      <div key={"ingress-" + index} style={{ marginBottom: "10px" }}>
        <div
          onClick={function () {
            setOpenIngressIndex(isOpen ? null : index);
          }}
          style={{
            background: theme.cardBg,
            borderRadius: "12px",
            // パディングを微調整
            padding: isMobile ? "12px 15px" : "15px 20px",
            border: "1px solid " + (isOpen ? borderColor : theme.border),
            borderLeft: "5px solid " + borderColor,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            cursor: "pointer",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div
              style={{
                width: "35px",
                display: "flex",
                justifyContent: "center",
                fontSize: "24px",
                color: planetColors[ing.planet] || theme.text,
              }}
            >
              {ing.planet === "Earth" ? (
                <Globe size={22} />
              ) : (
                PLANET_SYMBOLS[ing.planet] || "•"
              )}
            </div>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <div
                style={{
                  fontSize: "14px",
                  fontWeight: "bold",
                  color: theme.text,
                }}
              >
                {ing.planet} が{" "}
                {signData !== null && signData !== undefined
                  ? signData.name
                  : ""}{" "}
                へ移動
              </div>
              <div
                style={{
                  display: "flex",
                  // ★修正：モバイル時は日付とバッジを垂直に分ける
                  flexDirection: isMobile ? "column" : "row",
                  alignItems: isMobile ? "flex-start" : "center",
                  gap: isMobile ? "6px" : "10px",
                }}
              >
                <span
                  style={{
                    fontSize: "11px",
                    color: theme.subText,
                    fontWeight: "bold",
                  }}
                >
                  {formatDateTime(ing.date)} 発生
                </span>
                {/* バッジエリア */}
                <div style={{ display: "flex", gap: "5px", flexWrap: "wrap" }}>
                  {dignity !== null && dignity !== undefined && (
                    <span
                      style={{
                        fontSize: "9px",
                        padding: "2px 6px",
                        borderRadius: "4px",
                        background: dignity.score > 0 ? "#27ae60" : "#e74c3c",
                        color: "#fff",
                        fontWeight: "bold",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {dignity.label.split(" ")[0].toUpperCase()}
                    </span>
                  )}
                  {modality !== "不明" &&
                    QUALITY_STYLE[modality] !== undefined && (
                      <span
                        style={{
                          fontSize: "9px",
                          padding: "2px 6px",
                          borderRadius: "4px",
                          background: QUALITY_STYLE[modality].bg,
                          color: "#fff",
                          fontWeight: "bold",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {QUALITY_STYLE[modality].label}
                      </span>
                    )}
                </div>
              </div>
            </div>
          </div>
          {isOpen ? (
            <ChevronUp size={16} color={theme.subText} />
          ) : (
            <ChevronDown size={16} color={theme.subText} />
          )}
        </div>
        {isOpen === true && (
          <div
            style={{
              background: theme.subBg,
              padding: "15px 20px",
              border: "1px solid " + theme.border,
              borderTop: "none",
              borderRadius: "0 0 12px 12px",
              fontSize: "13px",
              color: theme.text,
            }}
          >
            <div
              style={{
                fontWeight: "bold",
                color: isDark ? "#60a5fa" : "#2c2d96",
                marginBottom: "4px",
              }}
            >
              市場への影響:
            </div>
            <div style={{ lineHeight: "1.6", fontWeight: "bold" }}>
              {INGRESS_MARKET_THEMES[ing.planet] || "解析データを取得中。"}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
      <div
        style={{
          padding: "10px 15px",
          background:
            isHeliocentric === true
              ? isDark
                ? "rgba(230, 103, 34, 0.1)"
                : "#fffaf0"
              : isDark
                ? "rgba(44, 45, 150, 0.1)"
                : "#f0f4ff",
          borderRadius: "8px",
          border:
            "1px solid " +
            (isHeliocentric === true ? "#e67e22" : "#2c2d96") +
            "33",
          display: "flex",
          alignItems: "center",
          gap: "10px",
        }}
      >
        <Info
          size={16}
          color={isHeliocentric === true ? "#e67e22" : "#2c2d96"}
        />
        <span
          style={{
            fontSize: "12px",
            fontWeight: "bold",
            color: isHeliocentric === true ? "#e67e22" : "#2c2d96",
          }}
        >
          分析モード: {isHeliocentric === true ? "ヘリオ" : "ジオ"} /{" "}
          {isSidereal === true ? "サイデリアル" : "トロピカル"}
        </span>
      </div>

      <div
        style={{
          display: "grid",
          // スマホ時は 1列(1fr)、PC時は自動フィット
          gridTemplateColumns: isMobile
            ? "1fr"
            : "repeat(auto-fit, minmax(300px, 1fr))",
          gap: isMobile ? "15px" : "20px",
        }}
      >
        {isHeliocentric === false && events.sukuyo && (
          <div
            style={{
              background: theme.cardBg,
              borderRadius: "15px",
              border: "1px solid " + theme.border,
              padding: "20px",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginBottom: "15px",
                fontWeight: "bold",
                color: theme.text,
              }}
            >
              <Moon size={18} color="#8b5cf6" /> <span>宿曜 (27宿)</span>
            </div>
            <div
              style={{
                padding: "20px",
                borderRadius: "12px",
                background: isDark ? "rgba(139, 92, 246, 0.1)" : "#f5f3ff",
                border:
                  "1px solid " +
                  (isDark ? "rgba(139, 92, 246, 0.2)" : "#ddd6fe"),
                display: "flex",
                alignItems: "center",
                gap: "20px",
              }}
            >
              <div
                style={{
                  width: "50px",
                  height: "50px",
                  borderRadius: "50%",
                  background: "#8b5cf6",
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "20px",
                  fontWeight: "bold",
                }}
              >
                {events.sukuyo.symbol}
              </div>
              <div>
                <div
                  style={{
                    fontSize: "22px",
                    fontWeight: "900",
                    color: isDark ? "#a78bfa" : "#5b21b6",
                  }}
                >
                  {events.sukuyo.name}
                </div>
                <div
                  style={{
                    fontSize: "11px",
                    color: isDark ? "#8b5cf6" : "#7c3aed",
                    fontWeight: "bold",
                  }}
                >
                  Moon's Mansion Status
                </div>
              </div>
            </div>
          </div>
        )}

        {isHeliocentric === false && (
          <div
            style={{
              background: theme.cardBg,
              borderRadius: "15px",
              border: "1px solid " + theme.border,
              padding: "20px",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginBottom: "15px",
                fontWeight: "bold",
                color: theme.text,
              }}
            >
              <Activity size={18} color="#e74c3c" />{" "}
              <span>Out of Bounds (赤緯異常)</span>
            </div>
            <div
              style={{ display: "flex", flexDirection: "column", gap: "10px" }}
            >
              {events.oobPlanets && events.oobPlanets.length > 0 ? (
                events.oobPlanets.map(function (p) {
                  return (
                    <div
                      key={p.planet}
                      style={{
                        background: theme.subBg,
                        borderRadius: "12px",
                        padding: "12px 15px",
                        border: "1px solid " + theme.border,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                        }}
                      >
                        <span
                          style={{
                            fontSize: "24px",
                            color: planetColors[p.planet] || "#e74c3c",
                          }}
                        >
                          {PLANET_SYMBOLS[p.planet]}
                        </span>
                        <div>
                          <div
                            style={{
                              fontSize: "14px",
                              fontWeight: "bold",
                              color: theme.text,
                            }}
                          >
                            {p.planet} OOB
                          </div>
                          <div
                            style={{ fontSize: "11px", color: theme.subText }}
                          >
                            赤緯 {p.declination.toFixed(2)}° (
                            {p.excess > 1.0 ? "最大値付近" : "警戒域"})
                          </div>
                        </div>
                      </div>
                      <div
                        style={{
                          fontSize: "10px",
                          fontWeight: "bold",
                          padding: "2px 8px",
                          borderRadius: "4px",
                          background: "rgba(231, 76, 60, 0.1)",
                          color: "#e74c3c",
                          border: "1px solid rgba(231, 76, 60, 0.2)",
                        }}
                      >
                        OOB
                      </div>
                    </div>
                  );
                })
              ) : (
                <div
                  style={{
                    padding: "15px",
                    textAlign: "center",
                    color: theme.subText,
                    fontSize: "11px",
                    background: "rgba(0,0,0,0.01)",
                    borderRadius: "8px",
                    border: "1px dashed " + theme.border,
                  }}
                >
                  境界外（OOB）の天体はありません
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {isHeliocentric === false &&
        events.solarTerms &&
        events.solarTerms.currentTerm && (
          <div
            style={{
              background: theme.cardBg,
              borderRadius: "15px",
              border: "1px solid " + theme.border,
              padding: "20px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginBottom: "15px",
                fontWeight: "bold",
                color: theme.text,
              }}
            >
              <Sun size={18} color="#27ae60" /> <span>二十四節気</span>
            </div>
            <div
              style={{
                padding: "20px",
                borderRadius: "12px",
                background: isDark ? "rgba(39, 174, 96, 0.1)" : "#f0fff4",
                border:
                  "1px solid " +
                  (isDark ? "rgba(39, 174, 96, 0.2)" : "#c6f6d5"),
                textAlign: "center",
              }}
            >
              <div
                style={{
                  fontSize: "28px",
                  fontWeight: "900",
                  color: "#27ae60",
                }}
              >
                {events.solarTerms.currentTerm.name}
              </div>
              <div
                style={{
                  fontSize: "12px",
                  color: theme.subText,
                  fontWeight: "bold",
                }}
              >
                {events.solarTerms.currentTerm.reading}
              </div>
            </div>
          </div>
        )}

      {isHeliocentric === false &&
        events.retrogrades &&
        events.retrogrades.length > 0 && (
          <div
            style={{
              background: theme.cardBg,
              borderRadius: "15px",
              border: "1px solid " + theme.border,
              padding: "20px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginBottom: "15px",
                fontWeight: "bold",
                color: theme.text,
              }}
            >
              <RefreshCw size={18} color="#e74c3c" /> <span>逆行中の惑星</span>
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
                gap: "10px",
              }}
            >
              {events.retrogrades.map(function (r, i) {
                return (
                  <div
                    key={"retro-" + i}
                    style={{
                      background: theme.subBg,
                      borderRadius: "12px",
                      padding: "15px",
                      border: "1px solid " + theme.border,
                      borderLeft: "5px solid #e74c3c",
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "24px",
                        color: planetColors[r.planet] || "#e74c3c",
                      }}
                    >
                      {PLANET_SYMBOLS[r.planet]}
                    </div>
                    <div>
                      <div
                        style={{
                          fontSize: "14px",
                          fontWeight: "bold",
                          color: theme.text,
                        }}
                      >
                        {r.planet} 逆行中
                      </div>
                      <div
                        style={{
                          fontSize: "10px",
                          color: theme.subText,
                          fontWeight: "bold",
                        }}
                      >
                        {r.sign !== undefined ? r.sign.name : ""}領域
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

      <div
        style={{
          background: theme.cardBg,
          borderRadius: "15px",
          border: "1px solid " + theme.border,
          padding: isMobile ? "15px" : "20px",
        }}
      >
        <div
          style={{
            display: "flex",
            // スマホ時は垂直、PC時は水平
            flexDirection: isMobile ? "column" : "row",
            alignItems: isMobile ? "flex-start" : "center",
            justifyContent: "space-between",
            gap: isMobile ? "10px" : "8px",
            marginBottom: "20px",
            fontWeight: "bold",
            color: theme.text,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <MoveRight size={18} color="#3498db" />{" "}
            <span>サイン・イングレス</span>
          </div>
          <div
            style={{
              fontSize: "11px",
              color: theme.subText,
              fontWeight: "normal",
            }}
          >
            直近のサイン移動を表示します
          </div>
        </div>

        {/* リストをグリッド化して、スマホ時は1列に */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile
              ? "1fr"
              : "repeat(auto-fill, minmax(280px, 1fr))",
            gap: "10px",
          }}
        >
          {events.ingresses !== undefined && events.ingresses !== null
            ? events.ingresses.slice(0, 10).map(function (ing, i) {
                return renderIngressCard(ing, i);
              })
            : null}
        </div>
      </div>

      <WeeklyCalendarSection
        baseDate={date}
        isHeliocentric={isHeliocentric}
        isSidereal={isSidereal}
        showExtendedPoints={showExtendedPoints}
        planetColors={planetColors}
        isDarkMode={isDark}
      />
    </div>
  );
}
